<template>
  <div class="mt-4 text-black tab-content">
    <v-col class="flex-wrap" cols="12">
      <div class="mt-4 mb-3 d-flex align-center" style="font-size: 32px">
        Tags
        <span>
          <help-dialog-workflow-settings-tags />
        </span>
      </div>
    </v-col>

    <v-col class="flex-wrap" cols="12">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mds:search"
        label="Search tags"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="mb-4"
      ></v-text-field>

      <v-data-table
        :headers="headers"
        :items="allTags"
        :search="search"
        :items-per-page="10"
        class="tags-table"
      >
        <template #item.name="{ item }">
          <div class="d-flex align-center">
            <v-icon icon="mds:label" size="small" class="mr-2"></v-icon>
            <span>{{ item.name }}</span>
          </div>
        </template>

        <template #item.count="{ item }">
          {{ item.count }} card{{ item.count !== 1 ? 's' : '' }}
        </template>

        <template #item.actions="{ item }">
          <v-btn
            icon="mds:delete"
            variant="text"
            size="small"
            color="error"
            :disabled="disabled"
            @click="confirmDeleteTag(item.name)"
          >
          </v-btn>
        </template>

        <template #no-data>
          <div class="text-center text-grey py-4">
            No tags found in this workflow
          </div>
        </template>
      </v-data-table>
    </v-col>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card class="pa-2">
        <v-card-title class="d-flex align-center text-red font-weight-bold">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Delete Tag
        </v-card-title>
        <v-card-text class="pt-2 px-3 pb-0">
          <div class="mb-2">
            Are you sure you want to delete the tag <strong>{{ tagToDelete }}</strong>?
          </div>
          <div class="mb-2">
            This will remove it from {{ getTagCount(tagToDelete) }} card{{ getTagCount(tagToDelete) !== 1 ? 's' : '' }}.
          </div>
          <div class="text-red font-weight-bold mb-2" style="font-size: 15px;">
            This operation cannot be undone.
          </div>
        </v-card-text>
        <v-card-actions class="py-1">
          <v-btn variant="tonal" @click="deleteDialog = false" :disabled="deleting">Cancel</v-btn>
          <v-btn color="red" variant="tonal" @click="deleteTag" :disabled="deleting">
            <template v-if="deleting">
              <v-progress-circular indeterminate color="white" size="18" class="mr-2"></v-progress-circular>
              Deleting...
            </template>
            <template v-else>
              Delete
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import HelpDialogWorkflowSettingsTags from '@/components/HelpDialogWorkflowSettingsTags.vue'

const props = defineProps({
  workflow: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const store = useStore()

const deleteDialog = ref(false)
const tagToDelete = ref(null)
const deleting = ref(false)
const search = ref('')

const headers = [
  { title: 'Tag Name', key: 'name', align: 'start', sortable: true },
  { title: 'Usage', key: 'count', align: 'start', sortable: true },
  { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '100' }
]

const storeWorkflow = computed(() => store.state.workflow.workflow)

const allTags = computed(() => {
  const tagCounts = new Map()

  if (storeWorkflow.value?.domainEvents) {
    storeWorkflow.value.domainEvents.forEach((event) => {
      if (event.cards) {
        event.cards.forEach((card) => {
          if (card.tags && Array.isArray(card.tags)) {
            card.tags.forEach((tag) => {
              tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1)
            })
          }
        })
      }
    })
  }

  return Array.from(tagCounts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

const confirmDeleteTag = (tagName) => {
  tagToDelete.value = tagName
  deleteDialog.value = true
}

const getTagCount = (tagName) => {
  const tag = allTags.value.find((t) => t.name === tagName)

  return tag ? tag.count : 0
}

const deleteTag = async () => {
  if (!tagToDelete.value) {
    return
  }

  deleting.value = true
  try {
    const cardsToUpdate = []

    if (storeWorkflow.value?.domainEvents) {
      storeWorkflow.value.domainEvents.forEach((event) => {
        if (event.cards) {
          event.cards.forEach((card) => {
            if (card.tags && card.tags.includes(tagToDelete.value)) {
              cardsToUpdate.push({
                cardId: card.id,
                tags: card.tags.filter((t) => t !== tagToDelete.value),
                version: card.version
              })
            }
          })
        }
      })
    }

    if (cardsToUpdate.length > 0) {
      await workflowApi.updateCardsMutation(props.workflow.id, cardsToUpdate)
      store.commit('snackbar/showMessage', {
        content: `Tag "${tagToDelete.value}" removed from ${cardsToUpdate.length} card${cardsToUpdate.length !== 1 ? 's' : ''}`,
        color: 'success'
      }, { root: true })
    }

    deleteDialog.value = false
    tagToDelete.value = null
  } catch (error) {
    console.error('Error deleting tag:', error)
    store.commit('snackbar/showMessage', {
      content: 'Error removing tag',
      color: 'error'
    }, { root: true })
  } finally {
    deleting.value = false
  }
}
</script>

<style scoped>
.tab-content {
  height: 900px;
}

.tags-table {
  border: 1px solid #E5E3E1;
  border-radius: 4px;
}

:deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: var(--light-1);
}
</style>
